import { Injectable } from '@angular/core';
import { map, Observable, Subject } from 'rxjs';
import { Experience, Filter, ExperienceShort, FilterText , SearchWhereResult } from './interfaces/experiences';
import { resource } from '../utils/resource/old';
import { Page } from './interfaces/pages';
import { TagsService } from '../utils/tags.service';
import { DWSTag, TagExpType, TagLang, TagService } from './interfaces/tags';

// DTOs from new API
interface ExperienceShortResponseDto {
  data: ExperienceShortDto[];
  page: number;
  size: number;
  total: number;
}

interface ExperienceShortDto {
  id: string;
  wineryId: string;
  winery: {
    name: string;
    city?: string;
  };
  details: {
    title: string;
    shortDescription?: string;
    duration?: string;
    place?: {
      lat: number;
      lng: number;
      city?: string;
      province?: string;
      region?: string;
      state?: string;
      street?: string;
      zipcode?: string;
    };
    instantBooking?: boolean;
    showWebsite?: boolean;
    showWidget?: boolean;
    enableGifts?: boolean;
    priority?: number;
    inquiry?: boolean;
    giftOnly?: boolean;
  };
  medias?: Array<{
    mediaUrl: string;
  }>;
  prices?: Array<{
    title: string;
    priceCents: number;
    priceCurrency: string;
    position: number;
  }>;
  category?: string;
}

interface ExperienceDto {
  id: string;
  wineryId: string;
  title: string;
  shortDescription?: string;
  details: {
    title: string;
    summary?: string;
    description?: string;
    shortDescription?: string;
    duration?: string;
    place?: {
      lat: number;
      lng: number;
      street?: string;
      city?: string;
      province?: string;
      region?: string;
      state?: string;
      zipcode?: string;
    };
    showWebsite?: boolean;
    showWidget?: boolean;
    instantBooking?: boolean;
    enableGifts?: boolean;
    minPartiesToEnable?: number;
    maxParties?: number;
    acceptPaypalPayments?: boolean;
    acceptStripePayments?: boolean;
  };
  prices?: Array<{
    price: number;
    priceCurrency: string;
    priceDescription?: string;
  }>;
  languagesIsoCode?: string[];
  medias?: any[];
  products?: any[];
  tags?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class ExperiencesService {

  locale: "it" | "de" | "en" = "it";
  types: TagExpType[];
  langs: TagLang[];
  services: TagService[];
  
  constructor(private _tagsService: TagsService) { 
    this.types = _tagsService.getAllTypes(this.locale);
    this.langs = _tagsService.getAllLang(this.locale);
    this.services = _tagsService.getAllServices(this.locale);
  }

  cleanExperiencesCard(card: Experience.Short[]): Experience.Short[] {
      // Verifica che card esista e sia un array
      if (!card || !Array.isArray(card)) {
        console.warn('cleanExperiencesCard: card is undefined, null, or not an array', card);
        return [];
      }
      
      return card.map((exp) => {
        // Verifica che exp.tags esista e sia un array prima di fare map
        if (exp.tags && Array.isArray(exp.tags)) {
          exp.tags = exp.tags.map(t => this.fixTags(t, this._tagsService));
          exp.dws_tags = exp.tags.map(t => this.converTags(t, this._tagsService));
        } else {
          // Se tags non esiste, inizializza come array vuoto
          exp.tags = [];
          exp.dws_tags = [];
        }
        return exp;
      });
  }

  fixTags(fixTags: Experience.Tag, _tagsService: TagsService): Experience.Tag {
    var id = fixTags.id;
    fixTags.category_code = _tagsService.getCategoryCodeFromId(fixTags.category_id) ;
    return fixTags;
  }

  converTags(fixTags: Experience.Tag, _tagsService: TagsService): DWSTag {
    var id = fixTags.id;
    return _tagsService.getTagFromId(id)!;
  }
  
  getExperienceById(experienceId: string): Observable<Experience> {
    return resource(`wsb2c://experience/byId`)
      .params({experienceId: experienceId})
      .get<Experience>();
  }

  // ============================================
  // VECCHIA VERSIONE CON MAPPER - COMMENTATA
  // ============================================
  /*
  getExperienceById(experienceId: string): Observable<Experience> {
    return resource(`divineaCom://experience/byId`)
      .params({experienceId: experienceId})
      .get<any>()
      .pipe(
        map((experience: any) => this.mapNewBackendToExperience(experience))
      );
  }
  */

  // ============================================
  // VECCHIA VERSIONE - COMMENTATA
  // ============================================
  /*
  getExperienceById(experienceId: string): Observable<Experience> {
    return resource(`divineaCom://experience/byId`)
      .params({experienceId: experienceId})
      .get<any>()
      .pipe(
        map((newBackendResponse: any) => this.mapNewBackendToExperience(newBackendResponse))
      );
  }
  */

  // ============================================
  // VECCHIO MAPPER - COMMENTATO
  // Ora usiamo direttamente il formato del nuovo backend
  // ============================================
  /*
  private mapNewBackendToExperience(experience: any): Experience {
    const details = experience.details || {};
    const place = details.place || {};
    
    // Mappa i tags da metaDatas (escludendo quelli che sono lingue, gestiti separatamente)
    const metaTags: Experience.Tag[] = (experience.metaDatas || [])
      .filter((meta: any) => !meta.metadataValue?.startsWith('lang_'))
      .map((meta: any) => ({
        id: meta.id?.toString() || '',
        name: meta.metadataValue || '',
        color: '',
        created_at: meta.updateTimestamp || '',
        updated_at: meta.updateTimestamp || '',
        category_id: '',
        visible: meta.active !== false,
        hardcoded: false,
        code: meta.metadataValue || '',
        name_translations: {} as Experience.Translation,
        category_code: ''
      }));

    // Mappa le lingue da languagesIsoCode creando tag con ID corretto
    // Il TagsService ha lingue con ID specifici, dobbiamo trovarli per codice
    const languageTags: Experience.Tag[] = (experience.languagesIsoCode || []).map((langCode: string) => {
      // Trova la lingua nel TagsService usando il codice (es: "DE" -> "de")
      const langCodeLower = langCode.toLowerCase();
      const langTag = this._tagsService.getAllLang('it').find(l => l.code === langCodeLower);
      
      if (langTag) {
        // Crea un tag con l'ID corretto che il componente può trovare con getTagFromId
        return {
          id: langTag.id, // ID corretto dal TagsService (es: "75843bcd-bf30-4bfa-8ea2-b6eee0484d57" per "de")
          name: langTag.name || '',
          color: langTag.color || '',
          created_at: '',
          updated_at: '',
          category_id: langTag.category_id || '',
          visible: true,
          hardcoded: false,
          code: langTag.code || '',
          name_translations: langTag.name_translations || {} as Experience.Translation,
          category_code: 'spklang' // IMPORTANTE: così viene filtrato correttamente nel componente
        };
      }
      
      // Fallback se la lingua non è trovata nel TagsService
      console.warn(`Lingua non trovata nel TagsService: ${langCode}`);
      return {
        id: '', // ID vuoto - getTagFromId non lo troverà
        name: langCode,
        color: '',
        created_at: '',
        updated_at: '',
        category_id: '',
        visible: true,
        hardcoded: false,
        code: langCodeLower,
        name_translations: {} as Experience.Translation,
        category_code: 'spklang'
      };
    });

    // Combina tutti i tag
    const tags: Experience.Tag[] = [...languageTags, ...metaTags];
    console.log('🔍 [ExperiencesService] Language tags creati:', languageTags);
    console.log('🔍 [ExperiencesService] Meta tags creati:', metaTags);
    console.log('🔍 [ExperiencesService] Tags combinati:', tags);

    // Mappa le photos da medias
    const photos: Experience.Photo[] = (experience.medias || []).map((media: any) => ({
      id: media.id?.toString() || '',
      imageable_type: 'experience',
      imageable_id: media.experienceId || '',
      cover: false,
      created_at: media.updateTimestamp || '',
      updated_at: media.updateTimestamp || '',
      image_data: {
        id: '',
        storage: '',
        metadata: {
          filename: media.mediaName || null,
          size: 0,
          mime_type: media.mime || '',
          width: 0,
          height: 0,
          public_id: media.mediaUrl || ''
        }
      },
      title: media.altName || media.mediaName || '',
      main: false,
      urls: {
        forEach: () => {},
        // TODO: Verificare con backend developer il base URL corretto per le immagini
        // Per ora uso l'URL relativo o completo se già presente
        original: media.mediaUrl || '',
        s: media.mediaUrl || '',
        m: media.mediaUrl || '',
        v: media.mediaUrl || '',
        l: media.mediaUrl || '',
        xl: media.mediaUrl || '',
        vv: media.mediaUrl || '',
        p: media.mediaUrl || ''
      }
    }));

    // Mappa i prezzi
    const firstPrice = experience.prices?.[0];
    const secondPrice = experience.prices?.[1];

    // Mappa place - assicurati che sia sempre definito
    const mappedPlace: Experience.Place = {
      id: experience.id || '',
      lat: (place?.lat !== undefined && place?.lat !== null) ? place.lat.toString() : '0',
      lng: (place?.lng !== undefined && place?.lng !== null) ? place.lng.toString() : '0',
      street: place?.street || '',
      city: place?.city || '',
      zipcode: place?.zipcode || '',
      province: place?.province || '',
      state: place?.state || '',
      region: place?.region || '',
      placeable_type: 'experience',
      placeable_id: experience.id || '',
      created_at: '',
      updated_at: details.updatedAt || '',
      location: {
        lat: (place?.lat !== undefined && place?.lat !== null) ? place.lat : 0,
        lon: (place?.lng !== undefined && place?.lng !== null) ? place.lng : 0
      }
    };

    // Mappa experience_price_labels da prices
    const experience_price_labels: Experience.ExperiencePriceLabel[] = (experience.prices || []).map((price: any, index: number) => ({
      id: index,
      title_translations: {} as Experience.Translation,
      price_cents: price.price || 0,
      price_currency: price.priceCurrency || price.priceSymbol || 'EUR',
      position: index,
      experience_id: experience.id || '',
      created_at: '',
      updated_at: '',
      quantity: 0
    }));

    // Costruisci l'oggetto Experience mappato
    const mappedExperience: Experience = {
      company: {
        wine_region_id: undefined
      },
      id: experience.id || '',
      title: details.title || '',
      summary: details.summary || '',
      description: details.description || '',
      company_id: experience.wineryId || '',
      price_cents: firstPrice?.price || 0,
      price_currency: firstPrice?.priceCurrency || firstPrice?.priceSymbol || 'EUR',
      price_description: secondPrice?.priceDescription || '',
      created_at: '',
      updated_at: details.updatedAt || '',
      duration_days: 0, // Da parsare da "1d 0h 0m" se necessario
      timetable: null,
      day_mon: false,
      day_tue: false,
      day_wed: false,
      day_thu: false,
      day_fri: false,
      day_sat: false,
      day_sun: false,
      title_translations: {} as Experience.Translation,
      summary_translations: {} as Experience.Translation,
      description_translations: {} as Experience.Translation,
      event_date: null,
      price_description_translations: {} as Experience.Translation,
      duration: details.duration || '',
      price01_label: firstPrice?.priceDescription || '',
      price01_label_translations: {} as Experience.Translation,
      price02_label: secondPrice?.priceDescription || '',
      price02_label_translations: {} as Experience.Translation,
      price02_cents: secondPrice?.price || 0,
      price02_currency: secondPrice?.priceCurrency || secondPrice?.priceSymbol || 'EUR',
      deleted_at: null,
      show_website: details.showWebsite !== false,
      show_widget: details.showWidget !== false,
      pay_at_the_winery: details.payAtTheWinery !== false,
      interval: 0, // Da parsare da "PT24H" se necessario
      checkout: null,
      meta_title_translations: {} as Experience.Translation,
      meta_description_translations: {} as Experience.Translation,
      notes: '',
      notice: 0, // Da parsare da "PT72H" se necessario
      instant_booking: details.instantBooking !== false,
      antecedence: 0, // Da parsare da "PT72H" se necessario
      active: details.active !== false,
      price02_active: !!secondPrice,
      accept_paypal_payments: details.acceptPaypalPayments !== false,
      accept_stripe_payments: details.acceptStripePayments !== false,
      state: details.state || '',
      min_parties_to_enable: details.minPartiesToEnable || 0,
      max_parties: details.maxParties || 0,
      days_for_automatic_approve: details.daysForAutomaticApprove || 0,
      enable_gifts: details.enableGifts !== false,
      short_description_translations: {
        de: '',
        en: '',
        it: ''
      },
      priority: details.priority || 0,
      is_inquiry: details.inquiry !== false,
      is_gift_only: details.giftOnly !== false,
      slot_automation_config: {},
      place: mappedPlace,
      photos: photos,
      products: experience.products || [],
      cover: photos[0] || {} as Experience.Cover,
      tags: tags,
      experience_price_extras: [],
      experience_price_labels: experience_price_labels
    };

    return mappedExperience;
  }
  */

  getWhereSearchResults(filter: FilterText): Observable<SearchWhereResult[]> {
    // TODO: Verificare endpoint esatto con backend developer
    // Pattern probabile: /api/public/search-where o /api/public/experience/search-where
    return resource(`divineaCom://experience/search-where`)
      .params(filter as any)  
      .get<SearchWhereResult[]>();
  }
 
  list(filter: Filter): Observable<Page<Experience>> {
    return resource('divineaCom://experience/search')
      //.params(filter as any)
      .post<Page<Experience>>(filter)
  }

  
  listCard(filter: Filter): Observable<Page<ExperienceShort>> {
    filter.short = 'true';
    return resource('wsb2c://experience/search')
      //.params(filter as any)
      .post<ExperienceShortResponseDto>(filter)
      .pipe(
        map((response) => this.mapExperienceShortResponseToPage(response, (dto) => this.mapExperienceShortDtoToShort(dto)))
      )
  }

  getListCard(filter: Filter): Observable<Page<ExperienceShort>> {
    filter.short = true;
    delete filter.geoShape;
    return resource('wsb2c://experience/')
      .params(filter as any)
      .get<Page<ExperienceShort>>()
  }
   // Nuovo metodo per ottenere tutte le regioni uniche dalle esperienze
   getAllRegionsFromExperiences(experiences: Experience[] | undefined | null): string[] {
    // Verifica che experiences esista e sia un array
    if (!experiences || !Array.isArray(experiences)) {
      console.warn('getAllRegionsFromExperiences: experiences is undefined, null, or not an array', experiences);
      return [];
    }

    const regions = experiences
      .map((experience) => experience.details?.place?.region) // Estrai la regione di ciascun luogo (se presente)
      .filter((region): region is string => !!region); // Filtra regioni non definite

    return Array.from(new Set(regions)); // Rimuovi i duplicati e restituisci un array di regioni uniche
  }

  // Mapper functions
  private mapFilterToExperienceBodyRequest(filter: Filter): any {
    const request: any = {
      page: filter.page || 0,
      size: filter.size || 20,
      lang: filter.lang || 'it'
    };

    if (filter.adults) {
      request.adults = filter.adults;
    }
    if (filter.dateFrom) {
      request.dateFrom = filter.dateFrom;
    }
    if (filter.dateTo) {
      request.dateTo = filter.dateTo;
    }
    if (filter.experienceLanguages && filter.experienceLanguages.length > 0) {
      request.experienceLanguages = filter.experienceLanguages;
    }
    if (filter.tags && filter.tags.length > 0) {
      request.tags = filter.tags;
    }
    
    // Handle geoShape (bounding box) - send directly to backend
    if (filter.geoShape && filter.geoShape.type === 'envelope' && filter.geoShape.coordinates) {
      request.geoShape = filter.geoShape;
    } else if (filter.latitude && filter.longitude) {
      // If lat/lng are set, use geo distance query
      request.latitude = filter.latitude;
      request.longitude = filter.longitude;
      request.radius = filter.radius || '20km';
    }

    return request;
  }

  private mapExperienceShortResponseToPage<T>(response: ExperienceShortResponseDto, mapper: (dto: ExperienceShortDto) => T): Page<T> {
    return {
      data: response.data.map(mapper),
      content: response.data.map(mapper),
      empty: response.data.length === 0,
      first: response.page === 0,
      last: response.page * response.size >= response.total,
      pageNumber: response.page,
      numberOfElements: response.data.length,
      size: response.size,
      totalElements: response.total,
      totalPages: Math.ceil(response.total / response.size),
      sort: []
    };
  }

  private mapExperienceShortDtoToShort(dto: ExperienceShortDto): ExperienceShort {
    const firstPrice = dto.prices && dto.prices.length > 0 ? dto.prices[0] : null;
    const secondPrice = dto.prices && dto.prices.length > 1 ? dto.prices[1] : null;
    const firstMedia = dto.medias && dto.medias.length > 0 ? dto.medias[0] : null;
    const place = dto.details?.place;

    return {
      id: dto.id,
      wineryId: dto.wineryId || '',
      category: dto.category || '',
      details: {
        duration: dto.details?.duration || '',
        title: dto.details?.title || '',
        shortDescription: dto.details?.shortDescription,
        place: place || {
          lat: 0,
          lng: 0,
          street: '',
          zipcode: '',
          city: '',
          province: '',
          region: '',
          state: ''
        },
        showWebsite: dto.details?.showWebsite || false,
        showWidget: dto.details?.showWidget || false,
        instantBooking: dto.details?.instantBooking || false,
        enableGifts: dto.details?.enableGifts || false,
        priority: dto.details?.priority || 0,
        inquiry: dto.details?.inquiry || false,
        giftOnly: dto.details?.giftOnly || false
      },
      medias: dto.medias || [],
      prices: dto.prices || [],
      // Campi opzionali per compatibilità
      cover: firstMedia?.mediaUrl || 'https://res.cloudinary.com/divinea/image/upload/v1/production/cache/lgdkpct7hn0skbfbupgi.jpg',
      img_url: firstMedia?.mediaUrl,
      title: dto.details?.title || '',
      short_description: dto.details?.shortDescription,
      city: place?.city,
      province: place?.province,
      tags: [], // Will be populated by cleanExperiencesCard if needed
      dws_tags: [],
      company_id: dto.wineryId,
      price01_label: firstPrice?.title,
      price_cents: firstPrice?.priceCents,
      price02_label: secondPrice?.title,
      price02_cents: secondPrice?.priceCents,
      instant_booking: dto.details?.instantBooking || false,
      location: place ? {
        coordinates: [place.lng, place.lat]
      } : { coordinates: [0, 0] },
      company: {
        id: dto.wineryId,
        name: dto.winery?.name || ''
      }
    };
  }

  // ============================================
  // FUNZIONI DI MAPPING - NON PIÙ USATE
  // Il backend ora restituisce direttamente la struttura corretta
  // ============================================
  /*
  private mapExperienceShortDtoToExperience(dto: ExperienceShortDto): Experience {
    const place = dto.details?.place;
    const firstPrice = dto.prices && dto.prices.length > 0 ? dto.prices[0] : null;
    const secondPrice = dto.prices && dto.prices.length > 1 ? dto.prices[1] : null;

    return {
      id: dto.id,
      wineryId: dto.wineryId,
      title: dto.details?.title || '',
      summary: '',
      description: '',
      price_cents: firstPrice?.priceCents || 0,
      price_currency: firstPrice?.priceCurrency || 'EUR',
      price_description: '',
      created_at: '',
      updated_at: '',
      duration_days: 0,
      timetable: null,
      day_mon: false,
      day_tue: false,
      day_wed: false,
      day_thu: false,
      day_fri: false,
      day_sat: false,
      day_sun: false,
      title_translations: {},
      summary_translations: {},
      description_translations: {},
      event_date: null,
      price_description_translations: {},
      duration: dto.details?.duration || '',
      price01_label: firstPrice?.title || '',
      price01_label_translations: {},
      price02_label: secondPrice?.title || '',
      price02_label_translations: {},
      price02_cents: secondPrice?.priceCents || 0,
      price02_currency: secondPrice?.priceCurrency || '',
      deleted_at: null,
      show_website: dto.details?.showWebsite || false,
      show_widget: dto.details?.showWidget || false,
      pay_at_the_winery: false,
      interval: 0,
      checkout: null,
      meta_title_translations: {},
      meta_description_translations: {},
      notes: '',
      notice: 0,
      instant_booking: dto.details?.instantBooking || false,
      antecedence: 0,
      active: true,
      price02_active: secondPrice != null,
      accept_paypal_payments: false,
      accept_stripe_payments: false,
      state: dto.details?.place?.state || '',
      min_parties_to_enable: 0,
      max_parties: 0,
      days_for_automatic_approve: 0,
      enable_gifts: dto.details?.enableGifts || false,
      short_description_translations: {
        de: '',
        en: '',
        it: dto.details?.shortDescription || ''
      },
      priority: dto.details?.priority || 0,
      is_inquiry: dto.details?.inquiry || false,
      is_gift_only: dto.details?.giftOnly || false,
      slot_automation_config: {},
      place: place ? {
        id: '',
        lat: place.lat?.toString() || '',
        lng: place.lng?.toString() || '',
        street: '',
        city: place.city || '',
        zipcode: '',
        province: place.province || '',
        state: '',
        region: place.region || '',
        placeable_type: '',
        placeable_id: '',
        created_at: '',
        updated_at: '',
        location: {
          lat: place.lat || 0,
          lon: place.lng || 0
        }
      } : {
        id: '',
        lat: '',
        lng: '',
        street: '',
        city: '',
        zipcode: '',
        province: '',
        state: '',
        region: '',
        placeable_type: '',
        placeable_id: '',
        created_at: '',
        updated_at: '',
        location: {
          lat: 0,
          lon: 0
        }
      },
      photos: [],
      products: [],
      cover: {} as any,
      tags: [],
      experience_price_extras: [],
      experience_price_labels: (dto.prices || []).map((price, index) => ({
        id: index,
        title_translations: {},
        price_cents: price.priceCents || 0,
        price_currency: price.priceCurrency || 'EUR',
        position: price.position || index,
        experience_id: dto.id,
        created_at: '',
        updated_at: '',
        quantity: 0
      })),
      company: {} as any
    };
  }

  private mapExperienceDtoToExperience(dto: ExperienceDto): Experience {
    const place = dto.details?.place;
    const firstPrice = dto.prices && dto.prices.length > 0 ? dto.prices[0] : null;

    return {
      id: dto.id,
      wineryId: dto.wineryId,
      title: dto.details?.title || dto.title || '',
      summary: dto.details?.summary || '',
      description: dto.details?.description || '',
      price_cents: firstPrice?.price || 0,
      price_currency: firstPrice?.priceCurrency || 'EUR',
      price_description: firstPrice?.priceDescription || '',
      created_at: '',
      updated_at: '',
      duration_days: 0,
      timetable: null,
      day_mon: false,
      day_tue: false,
      day_wed: false,
      day_thu: false,
      day_fri: false,
      day_sat: false,
      day_sun: false,
      title_translations: {},
      summary_translations: {},
      description_translations: {},
      event_date: null,
      price_description_translations: {},
      duration: dto.details?.duration || '',
      price01_label: '',
      price01_label_translations: {},
      price02_label: '',
      price02_label_translations: {},
      price02_cents: 0,
      price02_currency: '',
      deleted_at: null,
      show_website: dto.details?.showWebsite || false,
      show_widget: dto.details?.showWidget || false,
      pay_at_the_winery: false,
      interval: 0,
      checkout: null,
      meta_title_translations: {},
      meta_description_translations: {},
      notes: '',
      notice: 0,
      instant_booking: dto.details?.instantBooking || false,
      antecedence: 0,
      active: true,
      price02_active: false,
      accept_paypal_payments: dto.details?.acceptPaypalPayments || false,
      accept_stripe_payments: dto.details?.acceptStripePayments || false,
      state: dto.details!.place?.state || '',
      min_parties_to_enable: dto.details?.minPartiesToEnable || 0,
      max_parties: dto.details?.maxParties || 0,
      days_for_automatic_approve: 0,
      enable_gifts: dto.details?.enableGifts || false,
      short_description_translations: {
        de: '',
        en: '',
        it: dto.shortDescription || ''
      },
      priority: 0,
      is_inquiry: false,
      is_gift_only: false,
      slot_automation_config: {},
      place: place ? {
        id: '',
        lat: place.lat?.toString() || '',
        lng: place.lng?.toString() || '',
        street: place.street || '',
        city: place.city || '',
        zipcode: place.zipcode || '',
        province: place.province || '',
        state: place.state || '',
        region: place.region || '',
        placeable_type: '',
        placeable_id: '',
        created_at: '',
        updated_at: '',
        location: {
          lat: place.lat || 0,
          lon: place.lng || 0
        }
      } : {
        id: '',
        lat: '',
        lng: '',
        street: '',
        city: '',
        zipcode: '',
        province: '',
        state: '',
        region: '',
        placeable_type: '',
        placeable_id: '',
        created_at: '',
        updated_at: '',
        location: {
          lat: 0,
          lon: 0
        }
      },
      photos: [],
      products: dto.products || [],
      cover: {} as any,
      tags: dto.tags || [],
      experience_price_extras: [],
      experience_price_labels: (dto.prices || []).map((price, index) => ({
        id: index,
        title_translations: {},
        price_cents: price.price || 0,
        price_currency: price.priceCurrency || 'EUR',
        position: index,
        experience_id: dto.id,
        created_at: '',
        updated_at: '',
        quantity: 0
      })),
      company: {} as any
    };
  }
  */

}
